import React, { useState, useMemo } from 'react';
import { Box, Typography, Paper, Collapse, IconButton, Chip, Alert } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { AutoAwesome } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TicketList from '../TicketList';

const TaskGeneratorHistoryItem = ({ item }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const tasks = item.agent_output?.tasks || [];
  const isFailed = item.agent_output?.error || item.agent_output?.parseError;
  const errorMessage = item.agent_output?.error || item.agent_output?.parseError || t('agent.history.agentProcessingError');

  const transformedTickets = useMemo(() => {
    const raw = item.generated_tickets;
    if (!Array.isArray(raw) || raw.length === 0) return [];

    const map = {};
    raw.forEach((ticket) => {
      map[ticket.id] = {
        id: ticket.id,
        ticketNumber: ticket.ticket_number,
        title: ticket.title,
        type: ticket.task_type || 'Task',
        priority: ticket.priority || 'Medium',
        children: [],
      };
    });

    const roots = [];
    raw.forEach((ticket) => {
      if (ticket.parent_ticket_id && map[ticket.parent_ticket_id]) {
        map[ticket.parent_ticket_id].children.push(map[ticket.id]);
      } else {
        roots.push(map[ticket.id]);
      }
    });

    return roots;
  }, [item.generated_tickets]);

  return (
    <Paper elevation={0} className="requirement-history-item">
      <Box className="requirement-history-item-header" onClick={() => setExpanded((prev) => !prev)}>
        <Box className="requirement-history-item-header-left">
          <IconButton size="small" className="requirement-history-expand-button">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Box className="requirement-history-item-preview-section">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" className="ac-history-item-ticket-number">
                {item.ticket_number}
              </Typography>
              <Typography variant="body2" fontWeight="600" className="requirement-history-item-preview">
                {item.ticket_title}
              </Typography>
            </Box>
            {item.created_at && (
              <Typography variant="caption" className="requirement-history-item-date">
                {item.created_at}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className="requirement-history-item-stats">
          <Chip
            label={`${item.total_ticket_count ?? transformedTickets.length} ${t('agent.history.tickets')}`}
            size="small"
            className="requirement-history-stat-chip"
          />
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box className="requirement-history-item-content">
          {/* Generated Tasks */}
          <Box className="requirement-history-section">
            <Box className="agent-section-header">
              <Box className="agent-section-header-content">
                <AutoAwesome className="agent-section-header-icon" />
                <Typography variant="h6" className="agent-section-header-title">
                  {t('agent.taskGenerator.stepper.taskPreview', 'Task Preview')}
                </Typography>
              </Box>
            </Box>
            {isFailed ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : tasks.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tasks.map((task, idx) => (
                  <Paper
                    key={idx}
                    elevation={1}
                    className="breakdown-tree-item-paper"
                    data-level={0}
                    data-type="task"
                    sx={{ p: 2 }}
                  >
                    <Box className="breakdown-tree-item-header-left">
                      <Chip
                        label={`${t('agent.taskGenerator.task', 'Task')} ${idx + 1}`}
                        size="small"
                        className="breakdown-tree-item-type-chip breakdown-tree-item-type-chip-task"
                      />
                      <Typography variant="h6" className="breakdown-tree-item-title" data-level={0}>
                        {task.title}
                      </Typography>
                    </Box>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary" className="breakdown-tree-item-description">
                        {task.description}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" className="requirement-history-no-data">
                {t('agent.taskGenerator.noTasksGenerated', 'No tasks generated')}
              </Typography>
            )}
          </Box>

          {/* Generated Tickets */}
          {transformedTickets.length > 0 && (
            <Box className="requirement-history-section">
              <Box className="agent-section-header">
                <Box className="agent-section-header-content">
                  <AutoAwesome className="agent-section-header-icon" />
                  <Typography variant="h6" className="agent-section-header-title">
                    {t('agent.history.generatedTickets')}
                  </Typography>
                </Box>
              </Box>
              <Box className="requirement-history-tickets">
                <TicketList tickets={transformedTickets} hideHeader={true} />
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TaskGeneratorHistoryItem;
